
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CustomDatePickerProps {
  selectedDate: string;
  onChange: (date: string) => void;
  onClose: () => void;
  className?: string;
  parentRef?: React.RefObject<HTMLDivElement | null>;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({ selectedDate, onChange, onClose, className = '', parentRef }) => {
  const [currentDate, setCurrentDate] = useState(selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date());
  
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const startDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const changeMonth = (increment: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
  };

  const handleDayClick = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const formatted = date.toISOString().split('T')[0];
    onChange(formatted);
    onClose();
  };

  // Calculate position based on parent ref
  const getPosition = () => {
    if (!parentRef?.current) {
      return { top: 0, left: 0 };
    }
    const rect = parentRef.current.getBoundingClientRect();
    const dropdownWidth = 288; // w-72 = 18rem = 288px
    const wouldOverflowRight = rect.left + dropdownWidth > window.innerWidth;

    return {
      top: rect.bottom + 8,
      ...(wouldOverflowRight
        ? { right: window.innerWidth - rect.right }
        : { left: rect.left }
      ),
    };
  };

  const position = getPosition();

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 5 }}
      style={{
        position: 'fixed',
        top: position.top,
        ...('left' in position ? { left: position.left } : { right: position.right }),
        width: 288,
        zIndex: 200,
      }}
      className={`p-4 bg-white rounded-2xl shadow-xl border border-[#D1D5DB] ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <button onClick={(e) => { e.preventDefault(); changeMonth(-1); }} className="p-1 hover:bg-[#F3F4F6] rounded-lg text-[#6B7280]">
          <ChevronLeft size={18} />
        </button>
        <span className="font-bold text-[#111827]">
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <button onClick={(e) => { e.preventDefault(); changeMonth(1); }} className="p-1 hover:bg-[#F3F4F6] rounded-lg text-[#6B7280]">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs font-bold text-[#6B7280] py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {[...Array(startDay)].map((_, i) => <div key={`empty-${i}`} />)}
        {[...Array(daysInMonth)].map((_, i) => {
          const day = i + 1;
          const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toISOString().split('T')[0];
          const isSelected = selectedDate === dateStr;
          
          return (
            <button
              key={day}
              onClick={(e) => { e.preventDefault(); handleDayClick(day); }}
              className={`h-8 w-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                isSelected 
                  ? 'bg-[#5D1828] text-white shadow-md shadow-[#9E2A2F]/50' 
                  : 'text-[#374151] hover:bg-[#F3F4F6]'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};